import "./App.css";
import {
  useDispatch,
  //  useSelector
} from "react-redux";
import {
  AppDispatch,
  //  RootState
} from "@/store";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { Header } from "@/components/header/Header";
import Header2 from "@/components/header/Header2";
import SessionTimeoutDialog from "@/components/dialogs/dialog_session_timeout";
import Footer from "@/components/footer/Footer";
import { ThemeProvider } from "@/contexts/ThemeProvider";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ScrollArea, ScrollBar } from "./components/ui/scroll-area";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LoadingProvider } from "@/contexts/LoadingContext";
import Loader from "@/components/Loader";
import packagejson from "../package.json";
import {
  createTheme,
  ThemeProvider as MUIThemeProvider,
} from "@mui/material/styles";
import { TooltipProvider } from "./components/ui/tooltip";
import ErrorBoundary from "./contexts/ErrorBoundaryContext";
import ErrorPage from "./components/ErrorPage";
import "react-date-range/dist/styles.css"; // Main styles
import "react-date-range/dist/theme/default.css"; // Default theme
import { onMessageListener } from "./firebase";
import {
  logoutUser,
  reLoginUser,
  // reLoginUser,
  setNotification,
} from "./features/auth/UserSlice";
import { SocketProvider } from "./contexts/SocketContext";
import toastService from "./services/toastService";
import ViewportScaler from "./lib/view-port-scaler";
import { useEffect, useState } from "react";
import { MessagePayload } from "firebase/messaging";
import { isShareableRoute } from "./utils/firebaseUtils";
import ReLoginDialog from "./components/dialogs/dialog-relogin";


const formatUTCtoLocal = (value: string) => {
  // Add Z to force browser to treat it as UTC
  const utcDate = new Date(value.replace(" ", "T") + "Z");
  return utcDate.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const showBrowserNotification = (
  title: string,
  body = "",
  url = "",
  icon = "/logo192.png" // make sure this is correct path in public folder
) => {
  if (typeof Notification === "undefined") return;

  if (Notification.permission === "granted") {
    const notification = new Notification("BuildMapper", {
      body: `${title}${body ? ` - ${body}` : ""}`,
      icon, // icon shown in notification
      badge: icon, // optional: for Android devices
    });

    if (url) {
      notification.onclick = () => {
        window.focus();
        window.location.href = url;
      };
    }
  }
};

function App() {
  console.log("-------- app version --------", packagejson.version);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isReLoginDialogOpen, setIsReLoginDialogOpen] = useState(false);

  const navigate = useNavigate();


useEffect(() => {
  const handler = () => {
    setIsReLoginDialogOpen(true);

  };
  
  window.addEventListener("open-relogin-dialog", handler);
  return () => {
    window.removeEventListener("open-relogin-dialog", handler);
  };
}, []);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", (event) => {
        const { action, url } = event.data || {};
        if (action === "navigate" && url) {
          window.location.href = url;
        }
        if (action === "relogin") {
          navigate(url || "/profile/subscription");
          dispatch(reLoginUser({ platform: 3 }))
            .unwrap()
            .then(() => setIsReLoginDialogOpen(true))
            .catch((err) => console.error("Re-login failed", err));
        }
      });
    }
  }, []);

  // useEffect(() => {
  //   if (sessionStorage.getItem("triggerReLoginDialog") === "true") {
  //     console.log("line 113 inside dialog open");
  //     setIsReLoginDialogOpen(true);
  //     sessionStorage.removeItem("triggerReLoginDialog");
  //   }
  // }, []);

  const theme = createTheme({
    palette: {
      primary: {
        main: "#8fdda3", // Change primary color
      },
      secondary: {
        main: "#7dce93", // Change secondary color
      },
      // You can customize other colors as needed
      background: {
        default: "#f5f5f5", // Change default background color
      },
    },
    typography: {
      fontFamily: '"Outfit", "Arial", "sans-serif"',
      fontSize: 14,
    },
  });
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  // Check if we should initialize Firebase for this route
  const shouldUseFirebase = !isShareableRoute();

  // Handle incoming messages - only for non-shareable routes
  useEffect(() => {
    if (!shouldUseFirebase) return;

    if (
      typeof Notification !== "undefined" &&
      Notification.permission !== "granted"
    ) {
      Notification.requestPermission();
    }
  }, [shouldUseFirebase]);

  useEffect(() => {
    if (!shouldUseFirebase) return;

    const handleMessage = (payload: MessagePayload) => {
      console.log("Message received. ", payload.notification);
      console.log("Message payload", payload);

      const title = payload.data?.title ?? "";
      const body = payload.data?.body ?? "";
      const event = payload.data?.event;
      const icon = payload.data?.icon;

      // Your event-based handling logic
      if (event === "notification.lead_won_or_lost") {
        const leadId = payload.data?.leadId;
        const url = `/dashboard/leads/${leadId}`;
        showBrowserNotification(
          title || "Lead status updated",
          body,
          url,
          icon
        );
         if (!window.location.pathname.startsWith("/admin")) {
           toastService.info(title || "Lead status updated");
        }
       
      } else if (event === "notification.assignee_lead_assignment") {
        const leadId = payload.data?.leadId;
        const url = `/dashboard/leads/${leadId}`;
        showBrowserNotification(
          title || "Lead assigned to you",
          body,
          url,
          icon
        );
      } else if (event === "notification.save_leads_to_crm") {
        if (!window.location.pathname.startsWith("/admin")) {
          toastService.info(body);
        }
        
      } else if (event === "notification.update_company_profile") {
        showBrowserNotification(
          title || "Company profile updated",
          body,
          `/profile/company-profile`,
          icon
        );
      } else if (event === "notification.new_org_user_added") {
        showBrowserNotification(title || "New user added", body, " ", icon);
      } else if (event === "notification.update_org_user") {
        showBrowserNotification(
          title || "User profile updated",
          body,
          " ",
          icon
        );
      } else if (event === "notification.calendar_event") {
        const rawStart = payload.data?.start;
        const rawStop = payload.data?.stop;

        let localStart = "";
        let localStop = "";

        if (rawStart) localStart = formatUTCtoLocal(rawStart);
        if (rawStop) localStop = formatUTCtoLocal(rawStop);
        const newBody = `Event "${payload.data?.eventName}" has been updated. New schedule: ${localStart} to ${localStop}.`;

        showBrowserNotification(
          title || "Calendar Event",
          newBody,
          `/dashboard/calendar`,
          icon
        );
      } else if (event === "notification.lead_data_uploaded") {
        showBrowserNotification(
          title || "Leads uploaded",
          body,
          `/dashboard/explore_leads`,
          icon
        );
      } else if (event === "notification.builder_watchlist") {
        showBrowserNotification(
          title || "Leads uploaded",
          body,
          `/dashboard/explore_leads`,
          icon
        );
      } else if (event === "notification.logout") {
        dispatch(logoutUser()).then(() => {
          navigate("/auth/login");
          setIsLogoutDialogOpen(true);
        });
      }
         else if (event === "relogin") {
        dispatch(reLoginUser({ platform: 3 }))
          .unwrap()
          .then(() => {
            setIsReLoginDialogOpen(true);
          })
          .catch((err) => {
            console.error("Re-login API failed", err);

          });
      }
      else if (event === "notification.subscription_updated") {
        showBrowserNotification(
          title ,
          body,
          `/`,
          icon
        );
      } 
      else if (
        event === "notification.lead_enrichment" &&
        payload.data?.payload
      ) {
        const parsedObj = JSON.parse(payload.data.payload);
        const enrichedLeadId = parsedObj.leadId.toString();
        const url = `/dashboard/leads/${enrichedLeadId}`;
        showBrowserNotification(title || "Lead Enriched", body, url, icon);
        if (enrichedLeadId && location.pathname.includes(enrichedLeadId)) {
          window.location.reload();
        }
      }

      // Save latest notification in redux
      if (payload.notification) {
        dispatch(setNotification(payload.notification));
      }
    };

    // Register message listener
    onMessageListener(handleMessage);
  }, [shouldUseFirebase, dispatch, navigate, location.pathname]);

  // useEffect(() => {
  //   const handleError = (e: ErrorEvent | Event) => {
  //     const isErrorEvent = (e: any): e is ErrorEvent => "message" in e;

  //     if (isErrorEvent(e)) {
  //       const message = e.message || "";
  //       const filename = e.filename || "";

  //       const indicators = [
  //         "Loading chunk",
  //         "ChunkLoadError",
  //         "Importing a module script failed",
  //         "Module script load error",
  //         "unexpected token",
  //         // 'Cannot read properties of undefined',
  //         "Failed to fetch dynamically imported module",
  //         "Unexpected token <", // typical HTML returned as JS
  //         "text/html",
  //       ];

  //       if (
  //         indicators.some((indicator) => message.includes(indicator)) ||
  //         filename.endsWith(".js") ||
  //         filename.endsWith(".mjs")
  //       ) {
  //         console.error(
  //           " Detected critical JS loading error:",
  //           message,
  //           filename
  //         );
  //         setTimeout(() => {
  //           window.location.reload();
  //         }, 500);
  //       }
  //     } else if ((e.target as HTMLElement)?.tagName) {
  //       const target = e.target as HTMLElement;
  //       const tag = target.tagName.toLowerCase();
  //       const src = (target as any).src || (target as any).href;

  //       if (["script", "link", "img"].includes(tag)) {
  //         console.error(` Asset failed to load: ${tag} → ${src}`);
  //         // setTimeout(() => {
  //         //   window.location.reload();
  //         // }, 500);
  //       }
  //     }
  //   };

  //   window.addEventListener("error", handleError, true);
  //   return () => window.removeEventListener("error", handleError, true);
  // }, []);

  useEffect(() => {
    const handleError = (e: ErrorEvent | Event) => {
      //  Check flag to prevent repeated reloads
      if (sessionStorage.getItem("hasReloadedOnce") === "true") {
        console.warn(
          "Reload prevented — already reloaded once for critical error."
        );
        return;
      }

      const isErrorEvent = (e: any): e is ErrorEvent => "message" in e;

      if (isErrorEvent(e)) {
        const message = e.message || "";
        const filename = e.filename || "";

        const indicators = [
          "Loading chunk",
          "ChunkLoadError",
          "Importing a module script failed",
          "Module script load error",
          "Failed to fetch dynamically imported module",
        ];

        if (
          indicators.some((indicator) => message.includes(indicator)) ||
          filename.endsWith(".js") ||
          filename.endsWith(".mjs")
        ) {
          console.error(
            "Critical JS loading error detected:",
            message,
            filename
          );

          sessionStorage.setItem("hasReloadedOnce", "true");

          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      } else if ((e.target as HTMLElement)?.tagName) {
        const target = e.target as HTMLElement;
        const tag = target.tagName.toLowerCase();
        const src = (target as any).src || (target as any).href;

        if (["script", "link", "img"].includes(tag)) {
          console.error(`Asset failed to load: ${tag} → ${src}`);

          sessionStorage.setItem("hasReloadedOnce", "true");

          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      }
    };

    window.addEventListener("error", handleError, true);
    return () => window.removeEventListener("error", handleError, true);
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
        <MUIThemeProvider theme={theme}>
          <LoadingProvider>
            <TooltipProvider>
              <ErrorBoundary fallback={<ErrorPage />}>
                {/* ViewportScaler handles responsive scaling for different screen sizes */}
                <ViewportScaler />
                {/* SocketProvider enables real-time communication throughout the app */}
                <SocketProvider>
                  {/* Global loading indicator */}
                  <Loader />
                  {/* Main scrollable content area */}
                  <ScrollArea className="h-full">
                    {/* Show Header except on dashboard, profile and auth pages */}
                    {!location.pathname.includes("dashboard") &&
                      !location.pathname.includes("home_01") &&
                      !location.pathname.includes("profile") &&
                      // !location.pathname.includes("admin/login") &&
                      !location.pathname.includes("set-password") &&
                      !location.pathname.includes("redirect") &&
                      !location.pathname.includes("admin") &&
                      !location.pathname.includes("login") &&
                      !location.pathname.includes("newOnboarding") &&
                      !location.pathname.includes("auth") && <Header2 />}
                    {location.pathname.includes("home_01") && <Header />}
                    <SessionTimeoutDialog
                      open={isLogoutDialogOpen}
                      onOpenChange={setIsLogoutDialogOpen}
                      handleDelete={() => {
                        // Implement logout logic or redirect to login
                        window.location.reload(); // Or redirect to login route
                      }}
                      type="logout"
                    />
                    <ReLoginDialog
                      open={isReLoginDialogOpen}
                      onOpenChange={setIsReLoginDialogOpen}
                    />

                    {/* Router outlet for rendering child routes */}
                    <Outlet />
                    {/* Show Footer except on dashboard, profile and auth pages */}
                    {location.pathname.includes("home_01") && <Footer />}
                    {/* Toast notifications container */}
                    <ToastContainer />
                    {/* Custom scrollbar component */}
                    <ScrollBar className="z-50" />
                  </ScrollArea>
                </SocketProvider>
              </ErrorBoundary>
            </TooltipProvider>
          </LoadingProvider>
        </MUIThemeProvider>
      </ThemeProvider>
    </I18nextProvider>
  );
}

export default App;
